// BookSession.js
import React, { useState, useRef, useEffect } from 'react';
import { PageWrapper, BookSessionContainer, BookSessionSubHeading, Form, FormInput, FormGroup } from "./BookSession.elements";
import { Button } from '../../global-styles';
import './BookSession.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const BookSession = () => {
  const fullnameRef = useRef();
  const errRef = useRef();

  const [fullname, setName] = useState('');
  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');

  const [message, setMessage] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const [sessionType, setSessionType] = useState('coaching'); // default to coaching

  useEffect(() => {
    fullnameRef.current.focus();
  }, []);

  useEffect(() => {
    setValidEmail(EMAIL_REGEX.test(email));
  }, [email]);

  useEffect(() => {
    setErrMsg('');
  }, [fullname, email, message]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (type === 'freelance') {
      setSessionType('freelance');
    }
  }, []);

  useEffect(() => {
  const fetchAvailableDates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/counseling/available-dates`);
      const data = await response.json();
      setAvailableDates(data.map(d => new Date(d)));
    } catch (error) {
      console.error('Error fetching available dates:', error);
    }
  };

  if (sessionType === 'coaching') {
    fetchAvailableDates();
  }
}, [sessionType]);

  useEffect(() => {
  if (sessionType !== 'coaching' || !selectedDate) return;

  const fetchAvailableTimes = async () => {
    try {
      const formattedDate = selectedDate.toISOString().split("T")[0];
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/counseling/available-times/${formattedDate}`);
      const data = await response.json();
      setAvailableTimes(data);
    } catch (error) {
      console.error('Error fetching times:', error);
      setAvailableTimes([]);
    }
  };

  fetchAvailableTimes();
}, [selectedDate, sessionType]);

useEffect(() => {
  if (sessionType === 'freelance') {
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableTimes([]);
  }
}, [sessionType]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!EMAIL_REGEX.test(email)) {
      setErrMsg("Please enter a valid email.");
      return;
    }

    if (sessionType === 'coaching' && (!selectedDate || !selectedTime)) {
      setErrMsg('Please select both date and time.');
      return;
    }

    const bookingData = {
      fullName: fullname,
      email,
      message,
      date: null,
      time: null,
      sessionType
    };

    try {
      if (sessionType === 'freelance') {
      // Submit as freelance inquiry (no payment)
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/counseling/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        localStorage.setItem('freelanceInfo', JSON.stringify(bookingData));
        window.location.href =  '/freelance-success'; // Or your own route
      } else {
        setErrMsg('Failed to submit freelance inquiry. Please try again.');
      }
    } else {
      // Proceed to payment for coaching
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/payment/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
        fullName: fullname,
        email: email,
        message: message,
        date: selectedDate.toISOString().split("T")[0],
        time: selectedTime,
        sessionType
        })
      });

      const data = await response.json();
      if (response.ok && data.url) {
      // Store booking info locally for success page
        localStorage.setItem('bookingInfo', JSON.stringify({
          fullName: fullname,
          email,
          message,
          date: selectedDate.toISOString().split("T")[0],
          time: selectedTime
        }));

        window.location.href = data.url;
    }
    else {
        setErrMsg('Failed to initiate payment. Please try again.');
      }
    }
  } catch (err) {
    console.error('Payment session error:', err);
    setErrMsg('Server error. Please try later.');
  }
};

  return (
    <section>
      <PageWrapper>
        <BookSessionContainer>
          <BookSessionSubHeading>
            <p ref={errRef} className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">{errMsg}</p>
            <span>
              {sessionType === 'freelance' ? 'Freelance Inquiry Form' : 'Book a 1:1 Coaching Session'}
            </span>
          </BookSessionSubHeading>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <FormInput
                type="text"
                id="fullname"
                ref={fullnameRef}
                name="fullname"
                placeholder="Full Name"
                required
                value={fullname}
                onChange={e => setName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <FormInput
                type="email"
                id="email"
                name="email"
                placeholder="Email Address"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                onFocus={() => setEmailFocus(true)}
                onBlur={() => setEmailFocus(false)}
              />
              <p id="emailnote" className={emailFocus && email && !validEmail ? "instructions" : "offscreen"}>
                Enter a valid email address.
              </p>
            </FormGroup>

            <FormGroup>
              <label>Session Type</label>
              <select
                className="custom-select"
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value)}
                required
              >
                <option value="coaching">1:1 Coaching</option>
                <option value="freelance">Freelance Work Inquiry</option>
              </select>
            </FormGroup>

            <FormGroup>
              <FormInput
                as="textarea"
                rows="5"
                name="message"
                placeholder="Describe your needs or the type of session you're looking for..."
                required
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </FormGroup>

          {sessionType === 'coaching' && (
            <>
              <FormGroup>
                <label>Select Date</label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => {
                    setSelectedDate(date);
                    setSelectedTime('');
                  }}
                  minDate={new Date()}
                  placeholderText="Select a date"
                  includeDates={availableDates}
                  className="custom-input"
                />
              </FormGroup>

              {availableTimes.length > 0 && (
                <FormGroup>
                  <label>Select Time</label>
                  <select
                    className="custom-select"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    required
                  >
                    <option value="">-- Select a time --</option>
                    {availableTimes.map((time, idx) => (
                      <option key={idx} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </FormGroup>
            )}
          </>
        )}
          <Button className="w-100 btn btn-lg btn-primary" type="submit">
            {sessionType === 'freelance' ? 'Submit Inquiry' : 'Continue to Payment'}
          </Button>
          </Form>
        </BookSessionContainer>
      </PageWrapper>
    </section>
  );
};

export default BookSession;
